// src/components/TisProductsBrowser.js
import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { productsAdminApi } from "../utils/apiProducts";

function logProductsApiError(context, error) {
  console.error(context, {
    status: error?.response?.status,
    message: error?.message,
  });
}

function fmtPrice(n, cur = "EUR") {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  try {
    return new Intl.NumberFormat("sr-RS", {
      style: "currency",
      currency: cur || "EUR",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${cur || ""}`.trim();
  }
}

export default function TisProductsBrowser() {
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [hasImage, setHasImage] = useState(true);

  const [facets, setFacets] = useState({ brands: [], categories: [] });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(40);
  const [total, setTotal] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  async function loadFacets() {
    try {
      const { data } = await productsAdminApi.get("/api/tis/facets");
      setFacets({
        brands: Array.isArray(data?.brands) ? data.brands : [],
        categories: Array.isArray(data?.categories) ? data.categories : [],
      });
    } catch (e) {
      logProductsApiError("TIS facets error", e);
    }
  }

  async function loadList() {
    setLoading(true);
    try {
      const { data } = await productsAdminApi.get("/api/tis/products", {
        params: {
          page,
          pageSize,
          q: q || undefined,
          brand: brand || undefined,
          category: category || undefined,
          hasImage: hasImage ? 1 : 0,
          sort: "title",
          dir: "asc",
        },
      });
      setRows(data?.data || []);
      setTotal(data?.total || 0);
    } catch (e) {
      logProductsApiError("TIS list error", e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  // debounce search
  const debouncedReload = useMemo(
    () =>
      debounce(() => {
        setPage(1);
      }, 250),
    []
  );

  useEffect(() => {
    loadFacets();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line
  }, [page, pageSize, brand, category, hasImage]);

  useEffect(() => {
    debouncedReload();
    return () => debouncedReload.cancel();
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    // kad se page resetuje na 1 zbog q, list se učita iz efekta iznad?
    // ne, zato pozovi loadList kad se page promeni (već radi), ali q nije u deps.
    // rešenje: ručno loadList kad se q promeni i page==1 (ili direktno u debouncedReload).
    // Najjednostavnije: zovi loadList kad se q promeni + page==1.
    if (page === 1) loadList();
    // eslint-disable-next-line
  }, [q]);

  async function openDetails(row) {
    setSelected(row);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const { data } = await productsAdminApi.get(`/api/tis/products/${row.id}`);
      setDetails(data);
    } catch (e) {
      logProductsApiError("TIS details error", e);
    } finally {
      setDetailsLoading(false);
    }
  }

  function closeDetails() {
    setSelected(null);
    setDetails(null);
  }

  const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Search</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-72"
            placeholder="title / sku / url…"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Brand</label>
          <select
            value={brand}
            onChange={(e) => {
              setBrand(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-56"
          >
            <option value="">All</option>
            {facets.brands.map((b) => (
              <option key={b.brand} value={b.brand}>
                {b.brand} ({b.cnt})
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Category</label>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-72"
          >
            <option value="">All</option>
            {facets.categories.map((c) => (
              <option key={c.label} value={c.label}>
                {c.label} ({c.cnt})
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 px-2 py-2">
          <input
            type="checkbox"
            checked={hasImage}
            onChange={(e) => {
              setHasImage(e.target.checked);
              setPage(1);
            }}
          />
          <span className="text-sm">Has image</span>
        </label>

        <div className="ml-auto flex items-center gap-2">
          <div className="text-sm text-gray-500">
            {loading ? "Loading…" : `${total} items`}
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-2 py-2 border rounded dark:bg-neutral-800 dark:text-white"
          >
            {[20, 40, 80, 120, 200].map((n) => (
              <option key={n} value={n}>
                {n}/page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-white dark:bg-neutral-900">
            <tr className="text-left border-b">
              <th className="p-2 w-[64px]">Img</th>
              <th className="p-2">Title</th>
              <th className="p-2">SKU</th>
              <th className="p-2">Brand</th>
              <th className="p-2">Price</th>
              <th className="p-2 w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && !loading && (
              <tr>
                <td className="p-3 text-gray-500" colSpan={6}>
                  No results.
                </td>
              </tr>
            )}

            {rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-gray-50 dark:hover:bg-neutral-800">
                <td className="p-2">
                  {r.main_image ? (
                    <img
                      src={r.main_image}
                      alt=""
                      className="w-12 h-12 object-cover rounded border"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded border bg-gray-100 dark:bg-neutral-700" />
                  )}
                </td>
                <td className="p-2">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-gray-500">{r.unit ? `JM: ${r.unit}` : ""}</div>
                </td>
                <td className="p-2">{r.sku || ""}</td>
                <td className="p-2">{r.brand || ""}</td>
                <td className="p-2">
  {fmtPrice(r.price ?? r.display_price ?? r.contract_price ?? r.list_price, r.currency || "EUR")}
  {r.priceSource ? (
    <div className="text-[11px] text-gray-500">
      {r.priceSource === "variant" ? "from variants" : r.priceSource}
    </div>
  ) : null}
</td>
                <td className="p-2">
                  <button
                    className="px-2 py-1 border rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
                    onClick={() => openDetails(r)}
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center gap-2">
        <button
          className="px-3 py-2 border rounded disabled:opacity-50"
          onClick={() => setPage(1)}
          disabled={page <= 1}
        >
          «
        </button>
        <button
          className="px-3 py-2 border rounded disabled:opacity-50"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
        >
          Prev
        </button>
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Page {page} / {pageCount}
        </div>
        <button
          className="px-3 py-2 border rounded disabled:opacity-50"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page >= pageCount}
        >
          Next
        </button>
        <button
          className="px-3 py-2 border rounded disabled:opacity-50"
          onClick={() => setPage(pageCount)}
          disabled={page >= pageCount}
        >
          »
        </button>
      </div>

      {/* Details modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeDetails}>
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-lg max-w-4xl w-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-bold">{selected.title}</div>
                <div className="text-sm text-gray-500">{selected.sku} • {selected.brand}</div>
              </div>
              <button className="px-3 py-2 border rounded" onClick={closeDetails}>Close</button>
            </div>

            {detailsLoading && <div className="py-4 text-gray-500">Loading details…</div>}

            {details && !detailsLoading && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold mb-2">Images</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(details.images || []).map((im, idx) => (
                      <a key={idx} href={im.src || im.url} target="_blank" rel="noreferrer">
                        <img
                          src={im.src || im.url}
                          alt=""
                          className="w-full h-24 object-cover rounded border"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="font-semibold mb-2">Attributes</div>
                  <div className="max-h-72 overflow-auto border rounded">
                    <table className="min-w-full text-sm">
                      <tbody>
                        {(details.attributes || []).map((a, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 font-medium">{a.name}</td>
                            <td className="p-2">{a.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="font-semibold mt-4 mb-2">Price rows</div>
                  <div className="max-h-48 overflow-auto border rounded">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-white dark:bg-neutral-900">
                        <tr className="border-b">
                          <th className="p-2 text-left">JM</th>
                          <th className="p-2 text-left">List</th>
                          <th className="p-2 text-left">Disc%</th>
                          <th className="p-2 text-left">Contract</th>
                          <th className="p-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(details.priceRows || []).map((r, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{r.unit || ""}</td>
                            <td className="p-2">{fmtPrice(r.list_price, details.currency || "EUR")}</td>
                            <td className="p-2">{r.discount_pct ?? ""}</td>
                            <td className="p-2">{fmtPrice(r.contract_price, details.currency)}</td>
                            <td className="p-2">{fmtPrice(r.akcijska_price, details.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                  </div>

{Array.isArray(details?.variants) && details.variants.length > 0 && (
  <>
    <div className="font-semibold mt-4 mb-2">Variants</div>
    <div className="max-h-64 overflow-auto border rounded">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white dark:bg-neutral-900">
          <tr className="border-b">
            <th className="p-2 text-left">Name</th>
            <th className="p-2 text-left">SKU</th>
            <th className="p-2 text-left">Price</th>
          </tr>
        </thead>
        <tbody>
          {(details.variants || []).map((v, idx) => (
            <tr key={idx} className="border-b">
              <td className="p-2">
                {v.url ? (
                  <a className="text-blue-600 underline" href={v.url} target="_blank" rel="noreferrer">
                    {v.name}
                  </a>
                ) : (
                  v.name
                )}
                {v.section_title ? (
                  <div className="text-[11px] text-gray-500">{v.section_title}</div>
                ) : null}
              </td>
              <td className="p-2">{v.sku || ""}</td>
              <td className="p-2">{fmtPrice(v.price, v.currency || details.currency || "EUR")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
)}


                  <div className="mt-3">
                    <a className="text-blue-600 underline" href={details.url} target="_blank" rel="noreferrer">
                      Open on TIS
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
